// Custom hook for report generation logic
import { useState, useRef } from 'react';
import axios from 'axios';
import { API } from '../../../utils/api.js';
import { reportUtils } from '../utils/reportUtils.js';

/**
 * Helper function to convert text with bullet points to proper HTML
 * Handles: • bullets, - bullets, and regular paragraphs
 * Converts to proper <ul><li> tags for TiptapEditor compatibility
 */
const convertToHtml = (text) => {
  if (!text) return '';
  
  const lines = text.split('\n');
  let html = '';
  let inList = false;
  
  lines.forEach((line) => {
    const trimmedLine = line.trim();
    
    // Check if line starts with bullet (• or - followed by space)
    const isBullet = trimmedLine.startsWith('•') || /^-\s/.test(trimmedLine);
    
    if (isBullet) {
      // Remove bullet character and trim
      const content = trimmedLine.replace(/^[•]\s*/, '').replace(/^-\s+/, '').trim();
      
      if (!inList) {
        html += '<ul>';
        inList = true;
      }
      html += `<li><p>${content}</p></li>`;
    } else if (trimmedLine === '') {
      // Empty line - close list if open
      if (inList) {
        html += '</ul>';
        inList = false;
      }
    } else {
      // Regular paragraph
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      html += `<p>${trimmedLine}</p>`;
    }
  });
  
  // Close list if still open
  if (inList) {
    html += '</ul>';
  }
  
  return html;
};

// Fields that should be converted to HTML (rich text editor content)
const RICH_TEXT_FIELDS = new Set([
  // Executive Summary (Section 2)
  'projectOverview', 'keyMetrics', 'majorAchievements', 'challengesIssues', 'nextSteps',
  // Project Overview (Section 3)
  'projectDescription', 'scope', 'objectives', 'timeline', 'stakeholders', 'constraints', 'qualityStandards',
  // Progress Highlights (Section 4)
  'majorMilestones', 'notableAchievements', 'performanceImprovements', 'teamRecognition',
  // Work Completed (Section 5)
  'structuralWork', 'mepSystems', 'architecturalWork', 'siteWork', 'testingCommissioning',
  // Quality Inspections (Section 6)
  'overview', 'inspectionDetails', 'standardsCompliance', 'nonConformances', 'materialTesting', 'qualityImprovements',
  // Safety Compliance (Section 7)
  'incidentReports', 'complianceStatus', 'safetyInspections', 'ppeCompliance', 'safetyTraining', 'safetyImprovements',
  // Schedule Performance (Section 8)
  'criticalPath', 'scheduleVariances', 'milestonePerformance', 'resourceImpact', 'cashflowImpact', 'recoveryStrategies',
  // Resource Utilization (Section 9)
  'laborAnalysis', 'equipmentAnalysis', 'materialManagement', 'subcontractorStatus', 'optimizationStrategies', 'performanceBenchmarks',
  // Budget Analysis (Section 10)
  'varianceAnalysis', 'changeOrders', 'cashFlow', 'costForecast',
  // Risk Assessment (Section 11)
  'technicalDesignRisks', 'financialCommercialRisks', 'environmentalExternalRisks', 'operationalResourceRisks', 
  'otherRisks', 'highPriorityAnalysis', 'mitigationStrategies', 'contingencyPlans', 'riskMonitoring',
  // Next Phase (Section 14)
  'scopeOfWork', 'keyMilestones', 'riskManagement', 'qualitySafety', 'financialDetails', 'coordination'
]);

/**
 * Recursively process an object and convert only rich text content fields to HTML
 * Skips table data, photo labels, and other simple text fields
 */
const processDataForHtml = (data, key = '') => {
  if (!data) return data;
  
  if (typeof data === 'string') {
    // Only convert if this is a known rich text field
    if (RICH_TEXT_FIELDS.has(key)) {
      // Check if the string contains bullet points or multiple paragraphs
      if (data.includes('•') || /\n-\s/.test(data)) {
        return convertToHtml(data);
      }
      // For multi-paragraph content, wrap in <p> tags
      if (data.includes('\n\n') && !data.startsWith('<')) {
        return data.split('\n\n').map(p => `<p>${p.trim()}</p>`).join('');
      }
      // Single paragraph rich text
      if (!data.startsWith('<') && data.length > 50) {
        return `<p>${data}</p>`;
      }
    }
    // Return as-is for non-rich-text fields (tables, labels, etc.)
    return data;
  }
  
  if (Array.isArray(data)) {
    // Skip processing arrays of objects (likely table rows)
    if (data.length > 0 && typeof data[0] === 'object' && data[0] !== null) {
      return data; // Return table rows unchanged
    }
    return data.map(item => processDataForHtml(item, key));
  }
  
  if (typeof data === 'object') {
    const processed = {};
    for (const objKey in data) {
      processed[objKey] = processDataForHtml(data[objKey], objKey);
    }
    return processed;
  }
  
  return data;
};

export const useReportGeneration = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratedReportCurrent, setIsGeneratedReportCurrent] = useState(false);
  const [generatedReportData, setGeneratedReportData] = useState(null);
  const [showReportViewer, setShowReportViewer] = useState(false);
  const [showInlineViewer, setShowInlineViewer] = useState(false);
  const [currentReport, setCurrentReport] = useState(null);

  // Ref for report preview content (for PDF generation)
  const reportPreviewRef = useRef(null);

  // Fetch sites data
  const fetchSites = async () => {
    try {
      const response = await axios.get(`${API}/admin/sites`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch sites:', error);
      return [];
    }
  };

  // Fetch organization settings
  const fetchOrganizationSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/organization-settings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data || {
        companyName: 'G4 Design & Build',
        address: '456 Business Park, London, EC1A 1BB',
        phone: '+44 20 7123 4567',
        email: 'info@g4designbuild.com',
        website: 'www.g4designbuild.com'
      };
    } catch (error) {
      console.error('Error fetching organization settings:', error);
      // Return default value if fetch fails
      return {
        companyName: 'G4 Design & Build',
        address: '456 Business Park, London, EC1A 1BB',
        phone: '+44 20 7123 4567',
        email: 'info@g4designbuild.com',
        website: 'www.g4designbuild.com'
      };
    }
  };

  // Generate comprehensive report from all sections
  const generateComprehensiveReport = async (reportData, reportSections, selectedPeriod, selectedSite, selectedPhotos, photoLabels, currentReportId) => {
    try {
      setIsGenerating(true);
      
      // Simulate report generation (replace with actual API call)
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Create report data structure with null checks
      const reportPayload = {
        id: currentReportId || reportUtils.generateReportId(),
        title: reportData?.title || 'Construction Progress Report',
        summary: reportSections?.executiveSummary?.overview || '',
        dateRange: selectedPeriod || null,
        site: selectedSite || null,
        photos: (selectedPhotos || []).map(photoId => ({
          id: photoId,
          ...(photoLabels?.[photoId] || {})
        })),
        sections: reportSections || {},
        generatedAt: new Date().toISOString()
      };
      
      // Set generated report data
      setGeneratedReportData(reportPayload);
      setIsGeneratedReportCurrent(true);
      
      console.log('Report generated successfully');
      
      return reportPayload;
      
    } catch (error) {
      console.error('Error generating report:', error);
      throw error;
    } finally {
      setIsGenerating(false);
    }
  };

  // Legacy generate report function (for backward compatibility)
  const generateReport = async (reportData, selectedSite, sites, selectedPhotos, photoLabels, getAllPhotos) => {
    if (!reportData.title) return;
    
    setIsGenerating(true);
    
    try {
      const reportPayload = {
        id: `report_${Date.now()}`,
        title: reportData.title,
        summary: reportData.description, // Using description for legacy compatibility
        site: selectedSite,
        siteName: sites.find(s => s.id === selectedSite)?.site_name || 'Unknown Site',
        generatedAt: new Date().toISOString(),
        photos: (selectedPhotos || []).map(photoId => {
          const photo = getAllPhotos().find(p => p.id === photoId);
          return {
            id: photoId,
            label: photoLabels[photoId]?.label || '',
            description: photoLabels[photoId]?.description || '',
            imported: photo?.imported || false,
            name: photo?.name || '',
            url: photo?.url || ''
          };
        })
      };

      // Set current report for viewing
      setCurrentReport(reportPayload);
      setShowReportViewer(true);
      setIsGenerating(false);

      console.log('Report generated and ready for viewing:', reportPayload.title);
      
      return reportPayload;

    } catch (error) {
      console.error('Failed to generate report:', error);
      setIsGenerating(false);
      throw error;
    }
  };

  // Generate sample report data for testing
  const generateSampleReportData = () => {
    return {
      coverPage: {
        client: 'Meridian Properties LLC',
        contractor: 'G4 Design & Build', 
        reportAuthor: 'Sarah Chen, Site Engineer',
        reportDate: new Date().toLocaleDateString()
      },
      executiveSummary: {
        projectOverview: `This comprehensive monthly progress report covers the ongoing construction of the Meridian Commercial Tower, an 8-story Class A office building located in the Central Business District. The reporting period spans from ${new Date(new Date().getFullYear(), new Date().getMonth(), 1).toLocaleDateString('en-GB')} to ${new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toLocaleDateString('en-GB')}.

The project has achieved 85% overall completion, maintaining our position 3 days ahead of the baseline schedule. This month marked significant progress across multiple work fronts, with particular success in MEP rough-in activities and exterior envelope completion. The project team has demonstrated exceptional coordination, resulting in zero safety incidents and a quality score of 98%.

Key financial indicators remain positive, with the project tracking at 92% of budget utilization against 85% physical completion, indicating favorable cost performance. All major procurement activities are complete, with remaining materials either on-site or scheduled for just-in-time delivery.

Stakeholder satisfaction remains high, with the client expressing particular appreciation for the proactive communication approach and the quality of workmanship evident in completed areas. The project is well-positioned for successful completion within the contracted timeframe.`,
        
        keyMetrics: `• Overall Project Completion: 85% (Target: 82%)
• Schedule Performance Index (SPI): 1.03 (3 days ahead)
• Cost Performance Index (CPI): 1.08 (8% under budget)
• Tasks Completed This Period: 47 of 52 planned
• Safety Record: Zero incidents (180 consecutive days)
• Quality Score: 98% first-time inspection pass rate
• Labour Utilization: 95% efficiency achieved
• Material Waste: 1.8% (below 2% target)
• RFIs Processed: 23 closed, 4 pending
• Change Orders: 3 approved (£285,000 total value)
• Weather Days Lost: 2 days (recovered through overtime)
• Subcontractor Performance: 94% average rating`,
        
        majorAchievements: `• Structural Steel Installation: Completed full 8-story steel frame erection one week ahead of schedule with zero defects reported during third-party inspection. This milestone achievement was recognized by the client as exemplary performance.

• Exterior Envelope Progress: Achieved 90% completion of curtain wall installation on floors 1-6, with weather-tight conditions now established for interior work progression. Glazing installation exceeded quality expectations with zero leakage during water testing.

• MEP Rough-In Milestone: Electrical rough-in completed in Zones A through C, representing 75% of the building footprint. All circuits tested and approved by the electrical inspector. Plumbing systems pressure tested at 150% design pressure with zero failures.

• HVAC Installation Commenced: Successfully mobilized the HVAC subcontractor and completed 40% of main ductwork installation on floors 1-3. Equipment pad installation and vibration isolation systems completed and approved.

• Fire Protection Systems: Completed installation and testing of fire sprinkler systems on floors 1-4, achieving 100% pass rate on hydrostatic testing. Fire alarm backbone installation completed building-wide.

• Quality Recognition: Received commendation from the building inspector for exceptional concrete finish quality and reinforcement placement accuracy. Third-party quality audit scored 98.5%.`,
        
        challengesIssues: `• Weather Impact: Experienced 2 days of work stoppage due to heavy rainfall and high winds exceeding safe working limits for crane operations. Exterior cladding work was temporarily suspended, but the team implemented recovery measures including selective overtime and weekend work to maintain schedule.

• HVAC Equipment Delivery: The specialized air handling units experienced a 10-day delay at the manufacturer due to component shortages in the global supply chain. Mitigation measures included: expedited shipping arrangements, revised installation sequencing, and coordination with the electrical contractor to proceed with other work in affected areas.

• Skilled Labour Availability: Temporary shortage of certified welders during peak structural work was addressed through partnership with the local trade union and mobilization of additional crews from our regional network. No schedule impact resulted from this challenge.

• Design Coordination Issue: RFI #47 identified a conflict between the mechanical duct routing and structural beam at grid line F-7. Resolution achieved within 5 days through collaborative BIM coordination sessions, resulting in an approved design modification at no additional cost.

• Material Price Escalation: Steel reinforcement pricing increased 8% from original estimates. Impact absorbed within project contingency, with remaining contingency at 1.5% of contract value.`,
        
        nextSteps: `• Interior Finishes Commencement: Initiate drywall installation on floors 1-3 starting next week, with metal framing crew mobilizing Monday. Target completion of Level 1 drywall within 3 weeks.

• MEP Continuation: Complete electrical rough-in in remaining Zone D areas and commence fixture rough-in on lower floors. HVAC ductwork to progress to floors 4-5 following equipment delivery.

• Exterior Completion: Complete remaining 10% of curtain wall installation on floors 7-8. Begin exterior sealant and waterproofing final inspections.

• Elevator Installation: Mobilize elevator contractor for cab installation and controls programming. Shaft work 100% complete and ready for equipment.

• Quality Focus Areas: Implement enhanced inspection protocols for drywall installation. Schedule third-party thermal imaging inspection for exterior envelope verification.

• Upcoming Milestones: Building envelope completion (Target: End of Month), MEP rough-in completion (Target: Month +6 weeks), Interior finishes Phase 1 (Target: Month +8 weeks).

• Client Coordination: Schedule monthly progress review meeting with client stakeholders. Prepare detailed presentation on finishing material selections and colour schemes.`
      },
      projectOverview: {
        projectDescription: `The Meridian Commercial Tower is a state-of-the-art 8-story Class A office building designed to achieve LEED Gold certification. Located at 450 Central Business Park, the development represents a £8.5 million investment in premium commercial real estate.

Key building specifications include:
• Gross Floor Area: 125,000 square feet across 8 levels
• Net Lettable Area: 108,500 square feet
• Floor-to-floor height: 4.2 meters (typical floors)
• Ground floor retail: 8,500 square feet
• Basement parking: 180 spaces across 2 levels
• Building height: 38 meters to roof parapet

The architectural design features a contemporary glass curtain wall facade with high-performance glazing, achieving a U-value of 1.1 W/m²K. The building incorporates advanced sustainable features including rainwater harvesting, solar-ready roof infrastructure, and a high-efficiency building management system.

Structural system consists of a steel frame with composite metal deck floors, supported by reinforced concrete core walls and spread foundations on engineered fill. The design accommodates future tenant fit-out flexibility with 9-meter column-free spans.`,
        
        scope: `The contract scope encompasses full design-build delivery of the commercial office development, including:

Civil & Site Works:
• Site clearing, demolition of existing structures, and earthworks
• Deep foundation installation including driven steel piles
• Underground utilities connections (water, sewer, electrical, telecommunications)
• Stormwater management system with detention tank
• External landscaping, paving, and site furniture

Structural Works:
• Reinforced concrete foundations and basement construction
• Structural steel frame erection for 8 levels
• Composite metal deck flooring system
• Concrete core walls for stability and vertical circulation
• Roof steel and metal deck installation

Building Envelope:
• Curtain wall system with high-performance glazing
• Insulated metal panel cladding at service areas
• Roof waterproofing and insulation system
• Below-grade waterproofing and drainage

MEP Systems:
• Complete electrical distribution from incoming HV supply
• HVAC systems including chillers, AHUs, and VAV distribution
• Plumbing and drainage systems
• Fire protection including sprinklers and alarms
• Building management and controls system
• Vertical transportation (3 passenger lifts, 1 goods lift)

Interior Finishes (Base Building):
• Common area finishes to landlord specification
• Core toilet fit-out to each floor
• Lift lobby finishes and signage
• Fire stairs and corridors`,
        
        objectives: `Primary Project Objectives:

1. Schedule Performance: Deliver the completed building for occupancy by the contracted completion date, maintaining or improving upon the baseline programme.

2. Cost Control: Complete the project within the approved budget of £8.5 million, including appropriate contingency management for unforeseen conditions.

3. Quality Excellence: Achieve a minimum 95% first-time inspection pass rate across all disciplines, with zero critical defects at handover.

4. Safety Leadership: Maintain zero lost-time injuries throughout the project duration, fostering a safety-first culture among all project participants.

5. Sustainability Targets: Achieve LEED Gold certification through implementation of sustainable design features and construction practices.

6. Stakeholder Satisfaction: Maintain high levels of client satisfaction through proactive communication, quality workmanship, and responsive issue resolution.

7. Community Relations: Minimize construction impact on neighbouring properties through careful planning, noise management, and regular community updates.

8. Innovation Implementation: Successfully deploy BIM Level 2 coordination, drone progress monitoring, and digital quality management systems.`,
        
        timeline: `Project Duration: 18 months (78 weeks)
Contract Commencement: January 2024
Scheduled Completion: June 2025

Phase 1 - Enabling Works & Foundations (Months 1-4): COMPLETE
• Site establishment and temporary works
• Demolition and site clearance
• Piling and foundation construction
• Basement structure and waterproofing

Phase 2 - Structural Works (Months 4-9): COMPLETE
• Steel frame erection (8 levels)
• Composite floor deck installation
• Concrete core construction
• Roof structure completion

Phase 3 - Building Envelope & MEP Rough-In (Months 8-14): IN PROGRESS (85%)
• Curtain wall installation
• Roof waterproofing and plant installation
• MEP distribution and rough-in
• Fire protection systems

Phase 4 - Interior Finishes & Commissioning (Months 12-17): UPCOMING
• Internal partitions and ceilings
• MEP fit-off and commissioning
• Floor finishes and decorations
• Lift installation and testing

Phase 5 - Completion & Handover (Months 17-18): PLANNED
• Final inspections and certifications
• Defects rectification
• Client training and handover
• Practical completion`,
        
        stakeholders: `Project Client: Meridian Properties LLC
• Primary Contact: James Mitchell, Development Director
• Role: Project funding, design approval, tenant coordination

Main Contractor: G4 Design & Build Limited
• Project Director: Michael Thompson
• Project Manager: Sarah Chen
• Site Manager: John Smith
• Role: Design-build delivery, subcontractor coordination

Design Team:
• Architect: Johnson Architecture Associates (Lead Designer)
• Structural Engineer: Morrison Structural Consultants
• MEP Engineer: Henderson Building Services
• Role: Design development, technical approvals, site support

Key Subcontractors:
• Structural Steel: Atlas Steel Erectors Ltd
• MEP Installation: Integrated Building Services
• Curtain Wall: Premier Glazing Systems
• Lifts: Otis Elevator Company

Statutory Authorities:
• Local Planning Authority (Planning conditions discharge)
• Building Control (Inspections and approvals)
• Highways Authority (S278 works coordination)
• Utility Companies (Connection approvals)

Third-Party Consultants:
• Quantity Surveyor: Davis Langdon (Cost management)
• CDM Coordinator: SafeBuild Consultants
• LEED Consultant: Green Building Advisory`,
        
        constraints: `Schedule Constraints:
• Fixed completion date driven by client's pre-let agreements with anchor tenant
• Seasonal limitations on exterior work during winter months
• Lead times for specialized equipment (lifts: 16 weeks, chillers: 14 weeks)
• Coordination with adjacent property access requirements

Technical Constraints:
• Limited site footprint requiring careful logistics planning
• Existing underground utilities requiring protection and diversion
• Height restrictions near airport flight path (38m maximum)
• Ground conditions requiring engineered piling solution

Resource Constraints:
• Skilled labour availability for specialized MEP trades
• Tower crane reach limitations affecting material handling
• Site storage limitations requiring just-in-time deliveries
• Working hours restrictions (07:00-19:00 weekdays, 08:00-13:00 Saturdays)

Financial Constraints:
• Fixed-price contract with limited variation provisions
• Payment terms tied to milestone achievement
• Retention of 5% until end of defects liability period
• Performance bond requirements

Regulatory Constraints:
• Noise limitations adjacent to residential properties
• Dust and debris control requirements
• Traffic management plan compliance
• Environmental permit conditions for groundwater discharge`,
        
        qualityStandards: `The project adheres to comprehensive quality standards across all disciplines:

Structural Works:
• BS EN 1992 (Eurocode 2) for concrete structures
• BS EN 1993 (Eurocode 3) for steel structures
• NHBC standards for waterproofing and damp-proofing
• Concrete specification to BS 8500 Grade C40/50

Building Envelope:
• CWCT standards for curtain wall installation
• BS 6375 for window performance testing
• EPDM roofing to manufacturer's 25-year warranty specification
• Thermal performance to Building Regulations Part L

MEP Systems:
• BS 7671 (18th Edition) for electrical installations
• CIBSE guidelines for HVAC design and installation
• BS EN 12845 for sprinkler system installation
• BSRIA commissioning guidelines

Fire Safety:
• BS 9999 for fire safety design
• BS 5839 for fire detection and alarm systems
• BS EN 12101 for smoke control systems
• Third-party certification for fire-stopping

Quality Management:
• ISO 9001:2015 Quality Management System
• Project-specific Inspection and Test Plans (ITPs)
• Hold points for critical inspections
• Non-conformance reporting and close-out procedures
• Weekly quality audits by independent QA team`
      },
      progressHighlights: {
        majorMilestones: `COMPLETED MILESTONES:

✓ Site Establishment & Enabling Works (100%)
   Completed: March 2024 | Status: 2 weeks early
   All temporary facilities, site security, and welfare established

✓ Piling & Foundation Works (100%)
   Completed: April 2024 | Status: On schedule
   187 driven steel piles installed, pile caps and ground beams complete

✓ Basement Construction (100%)
   Completed: June 2024 | Status: 1 week early
   Two-level basement with waterproofing and MEP risers

✓ Structural Steel Frame (100%)
   Completed: September 2024 | Status: 1 week early
   Full 8-story frame erected with composite deck

✓ Roof Structure & Waterproofing (95%)
   Target: November 2024 | Status: On track
   Primary membrane complete, plant deck and edge details ongoing

IN PROGRESS MILESTONES:

◐ Curtain Wall Installation (90%)
   Target: December 2024 | Status: On track
   Floors 1-6 complete, floors 7-8 in progress

◐ MEP Rough-In (75%)
   Target: January 2025 | Status: 2 days ahead
   Electrical and plumbing on track, HVAC progressing

UPCOMING MILESTONES:

○ Building Envelope Completion
   Target: December 2024

○ Interior Finishes Commencement
   Target: December 2024

○ MEP Second Fix
   Target: February 2025

○ Lift Installation Complete
   Target: March 2025`,
        
        notableAchievements: `CONSTRUCTION ACHIEVEMENTS:

1. Structural Steel Excellence
The steel erection team achieved an exceptional milestone by completing the full 8-story structural frame one week ahead of programme. The installation involved 850 tonnes of structural steel, 12,000 bolted connections, and zero defects during third-party weld inspections. This achievement was recognized in the monthly client newsletter as exemplary performance.

2. Safety Leadership
The project has now achieved 180 consecutive days without a lost-time injury, representing over 156,000 labour hours worked safely. This accomplishment reflects the team's commitment to our "Target Zero" safety programme and was acknowledged with a safety excellence award from our insurance partners.

3. Quality Recognition
The most recent Building Control inspection resulted in zero observations, with the inspector noting the exceptional quality of concrete finish and reinforcement placement. Our internal quality score has consistently exceeded 95% across all trades.

4. Sustainable Construction Practices
Material waste has been maintained below 2% through careful planning and the implementation of our waste minimization programme. Recycling rates have achieved 94%, exceeding the LEED Gold requirement of 75%.

5. Innovation Implementation
Successfully deployed BIM Level 2 coordination across all design disciplines, resulting in the identification and resolution of 47 potential clashes before construction. Drone progress monitoring has improved stakeholder communication and documentation quality.

TEAM RECOGNITION:

• Site Manager John Smith - Leadership Excellence Award for exceptional coordination during structural phase
• Electrical Foreman David Wilson - Quality Achievement for zero-defect cable installation
• Safety Officer Mike Johnson - Safety Champion for developing enhanced working at height protocols
• Project Engineer Lisa Park - Innovation Award for BIM clash detection process improvement`,
        
        performanceImprovements: `PROCESS IMPROVEMENTS IMPLEMENTED:

1. BIM 5D Scheduling Integration
Implemented advanced 4D/5D BIM capabilities linking the construction programme to the 3D model and cost data. Benefits achieved:
• 15% reduction in coordination meeting duration
• Real-time progress visualization for stakeholders
• Improved resource loading accuracy
• Enhanced change order impact assessment

2. Drone Progress Monitoring
Deployed weekly drone surveys for progress documentation and site logistics optimization:
• Aerial progress photography for client reporting
• Material stockpile volume calculations
• Safety observation identification
• Time-lapse documentation for marketing

3. Digital Quality Management
Transitioned from paper-based to digital inspection and test records:
• 40% reduction in documentation processing time
• Real-time defect tracking and close-out
• Improved audit trail and compliance evidence
• Reduced rework through immediate feedback

4. Lean Construction Principles
Implemented Last Planner System for production planning:
• Plan reliability improved from 72% to 89%
• Reduced waiting time between trades by 25%
• Improved subcontractor coordination
• Weekly constraint analysis and removal

5. Enhanced Safety Protocols
Developed site-specific safety innovations:
• Colour-coded zone system for hazard awareness
• Daily stretch and flex programme
• Toolbox talk tracking and effectiveness measurement
• Near-miss reporting incentive programme`,
        
        teamRecognition: `MONTHLY RECOGNITION AWARDS:

Safety Champion - Mike Johnson, Safety Officer
For developing and implementing the enhanced working-at-height protocols that have contributed to our 180-day incident-free record. Mike's proactive approach to identifying and mitigating risks has set a new standard for safety leadership on site.

Quality Excellence - David Wilson, Electrical Foreman
For achieving zero defects across 15,000 metres of cable installation and 340 distribution board connections. David's attention to detail and commitment to first-time quality has resulted in 100% inspection pass rate for electrical work.

Innovation Leader - Lisa Park, Project Engineer
For driving the implementation of our BIM clash detection process, which identified and resolved 47 coordination issues before they reached the field. Lisa's work has saved an estimated 120 labour hours and prevented costly rework.

Team Spirit - Structural Steel Crew (Atlas Steel Erectors)
For exceptional collaboration and communication during the accelerated steel erection phase. The team's flexibility and can-do attitude enabled completion one week ahead of schedule while maintaining zero safety incidents.

Subcontractor of the Month - Premier Glazing Systems
For consistently exceeding quality expectations on curtain wall installation and maintaining excellent housekeeping standards. Their professionalism and craftsmanship have been noted by the client during site visits.`,
        
        // KPI metrics for sliders
        overallCompletion: 85,
        schedulePerformance: 103,
        budgetPerformance: 98,
        qualityScore: 96,
        safetyScore: 100
      },
      workCompleted: {
        structuralWork: `STRUCTURAL WORKS COMPLETED THIS PERIOD:

Foundation & Substructure:
All foundation works were completed in previous periods. This month focused on final inspections and sign-off of basement waterproofing systems, with the 10-year warranty documentation now in place.

Structural Steel Frame:
• Completed erection of the final steel members at roof level, including the plant room framing and roof edge steelwork
• Installed 47 tonnes of secondary steelwork for roof plant supports
• Completed all 850 bolted connections with torque verification documentation
• Third-party ultrasonic testing of 15% sample welds achieved 100% pass rate
• Applied intumescent fire protection to all primary steel members (floors 5-8)
• Installed steel stairs to all floors with anti-slip nosings

Composite Floor Deck:
• Completed installation of composite metal deck to roof level
• Poured 420m³ of lightweight concrete topping to floors 6-8
• Achieved 28-day cube strengths exceeding design requirements (42 N/mm²)
• Installed mesh reinforcement and edge trim details
• Completed penetration sleeves for MEP risers

Concrete Core:
• Poured final lift of concrete to core walls at roof level
• Completed lift shaft construction including guide rail fixing brackets
• Installed precast stair flights to all levels
• Applied waterproof membrane to lift pits
• Completed core door frame installations

Roof Structure:
• Installed roof deck steelwork and metal decking
• Completed upstand construction for roof plant area
• Installed fall protection anchor points to roof
• Prepared substrate for waterproofing membrane application`,
        
        mepSystems: `MEP SYSTEMS PROGRESS THIS PERIOD:

Electrical Installation:
• Completed electrical rough-in in Zones A, B, and C (75% of building)
• Installed main electrical riser cables from basement to Level 8
• Completed distribution board installation on floors 1-5
• Installed 15,000 metres of containment (cable tray and trunking)
• Pulled 23,000 metres of power and data cables
• Installed emergency lighting wiring to all completed areas
• Tested and commissioned fire alarm system backbone
• Completed earthing and bonding installation

Mechanical Installation:
• Installed 65% of main HVAC ductwork on floors 1-4
• Completed chilled water and heating pipework risers
• Installed 12 fan coil units on floors 1-2 (pilot installation)
• Completed plant room equipment pad foundations
• Installed ductwork insulation to completed sections
• Pressure tested all completed pipework sections (150% design pressure)
• Installed BMS sensors and actuators to completed systems

Plumbing & Drainage:
• Completed above-ground drainage installation to floors 1-6
• Installed domestic water risers and branch connections
• Completed hot water plant installation in basement
• Installed rainwater drainage system to roof
• Tested all drainage systems with water and air tests
• Completed sanitary fixture rough-in to floors 1-4

Fire Protection:
• Completed sprinkler installation on floors 1-5
• Hydrostatic tested all completed sprinkler zones (100% pass)
• Installed fire pump and jockey pump in basement plant room
• Completed fire hose reel installations to all floors
• Installed smoke dampers and fire/smoke detection in ductwork
• Completed fire stopping to all penetrations in floors 1-4`,
        
        architecturalWork: `ARCHITECTURAL WORKS PROGRESS THIS PERIOD:

External Envelope:
• Completed curtain wall installation on floors 1-6 (units 1-720 of 800)
• Installed entrance canopy steelwork and glazing
• Completed external door installations (main entrance and loading dock)
• Applied external sealant to completed curtain wall sections
• Installed louvres to plant room areas
• Completed roof edge capping and flashings
• Installed bird deterrent systems to roof parapets

Internal Partitions:
• Commenced metal stud framing to floors 1-2
• Installed core wall linings to stair and lift lobbies (floors 1-4)
• Completed shaft wall construction around MEP risers
• Installed fire-rated partitions to plant rooms
• Commenced acoustic insulation installation to external walls

Ceilings & Soffits:
• Installed ceiling grid framework to floors 1-2 common areas
• Completed bulkhead construction at lift lobbies
• Prepared ceiling voids for MEP services coordination
• Installed access panels for maintenance requirements

Doors & Ironmongery:
• Installed fire doors to stair cores (all floors)
• Completed door frame installations to floors 1-4
• Installed ironmongery to completed doors (closers, handles, locks)
• Prepared door schedules for final fit-out phase

Floor Finishes (Base Building):
• Completed concrete floor sealing to plant rooms
• Applied floor hardener to basement parking areas
• Prepared floor surfaces for final finish application
• Installed floor construction joints and movement joints`,
        
        siteWork: `SITE WORKS PROGRESS THIS PERIOD:

External Works:
• Completed permanent stormwater drainage connections
• Installed underground detention tank (450m³ capacity)
• Completed external lighting conduit installation
• Installed vehicle crossover and footpath connections
• Applied base course to car park areas
• Commenced landscape kerbing installation

Utilities & Services:
• Completed permanent electrical supply connection (1.5MVA)
• Installed gas main connection and metering
• Completed telecommunications duct installation
• Connected permanent water supply and fire hydrant
• Installed external grease trap and pump station
• Completed CCTV drainage survey (100% pass)

Temporary Works:
• Maintained site hoarding and security systems
• Operated tower crane for material handling (final month of use)
• Maintained temporary power and water supplies
• Managed site welfare facilities
• Coordinated skip and waste management

Site Logistics:
• Managed just-in-time material deliveries
• Coordinated subcontractor site access and parking
• Maintained clean site roads and washing facilities
• Operated material hoist for vertical transportation
• Managed laydown areas and material storage`,
        
        testingCommissioning: `TESTING & COMMISSIONING ACTIVITIES THIS PERIOD:

Electrical Testing:
• Completed initial verification testing to floors 1-5
• Tested 340 distribution board circuits (100% pass)
• Completed earth loop impedance testing
• Verified emergency lighting operation and duration
• Tested fire alarm system functionality
• Documented all test results in O&M manual format

Mechanical Testing:
• Pressure tested chilled water systems (1.5x design pressure)
• Leak tested all ductwork connections
• Verified fan coil unit operation (floors 1-2)
• Tested BMS point-to-point communication
• Calibrated temperature and pressure sensors
• Documented commissioning data for O&M records

Fire Protection Testing:
• Completed hydrostatic testing of sprinkler systems (floors 1-5)
• Tested fire pump operation and performance
• Verified sprinkler head coverage and spray patterns
• Tested fire alarm cause and effect matrix
• Documented test certificates for Building Control

Structural Testing:
• Completed concrete cube testing (28-day strengths)
• Verified steel connection torque values
• Conducted ultrasonic weld testing (15% sample)
• Completed floor flatness surveys (met FF50/FL30)
• Documented all test results and certifications

Building Envelope Testing:
• Completed water penetration testing to curtain wall (floors 1-4)
• Verified thermal performance of glazing system
• Tested smoke seals and fire stopping installations
• Documented warranty information and certifications`
      },
      qualityInspections: {
        overview: `QUALITY MANAGEMENT OVERVIEW:

The project maintains a comprehensive quality management system aligned with ISO 9001:2015 requirements and project-specific quality standards. Daily quality inspections are conducted by the site quality team, with weekly comprehensive audits performed by the independent QA manager.

This reporting period has achieved a 98% first-time inspection pass rate, exceeding our target of 95%. A total of 127 formal inspections were conducted, with 124 passing on first attempt. The 3 failed inspections were minor non-conformances that were rectified and re-inspected within 48 hours.

Key quality achievements this period include:
• Zero critical defects identified
• 100% compliance with specification requirements
• All concrete test results exceeding design requirements
• Third-party weld testing achieved 100% pass rate
• Building Control inspections completed with zero observations

The quality culture on site continues to strengthen, with all trades demonstrating commitment to right-first-time delivery. Weekly quality workshops have been effective in sharing lessons learned and best practices across the project team.`,
        
        inspectionDetails: `INSPECTION STATISTICS THIS PERIOD:

Structural Inspections: 45 completed
• Concrete pour inspections: 12 (all passed)
• Steel connection inspections: 18 (all passed)
• Weld inspections: 8 (all passed)
• Fire protection coating: 7 (all passed)

MEP Inspections: 32 completed
• Electrical installation: 14 (13 passed, 1 minor issue resolved)
• Mechanical systems: 10 (all passed)
• Plumbing and drainage: 8 (7 passed, 1 re-test required)

Building Envelope: 22 completed
• Curtain wall installation: 15 (all passed)
• Waterproofing inspections: 4 (all passed)
• Sealant application: 3 (all passed)

Fire Stopping: 18 completed
• Penetration sealing: 12 (11 passed, 1 rework required)
• Fire door installation: 6 (all passed)

Material Testing: 28 tests conducted
• Concrete cube tests: 18 (all exceeded requirements)
• Steel certifications: 6 (all compliant)
• Waterproofing tests: 4 (all passed)

Third-Party Inspections: 8 conducted
• Building Control: 3 (all approved)
• Structural engineer: 2 (all approved)
• Fire engineer: 2 (all approved)
• Lift consultant: 1 (approved)`,
        
        standardsCompliance: `STANDARDS COMPLIANCE STATUS:

Building Regulations:
✓ Part A (Structure) - Full compliance verified by structural engineer
✓ Part B (Fire Safety) - Ongoing compliance, fire strategy approved
✓ Part C (Contamination) - Ground conditions addressed, verification complete
✓ Part D (Toxic Substances) - Material certifications in place
✓ Part E (Acoustic) - Testing scheduled for fit-out phase
✓ Part F (Ventilation) - Design compliance, commissioning pending
✓ Part G (Sanitation) - Installation progressing to approved design
✓ Part H (Drainage) - CCTV survey completed, 100% compliance
✓ Part J (Heat Producing) - Not applicable
✓ Part K (Protection) - Balustrades and barriers compliant
✓ Part L (Energy) - Thermal performance verified, EPC pending
✓ Part M (Access) - DDA compliance ongoing
✓ Part P (Electrical) - Installation to BS 7671, testing ongoing

Industry Standards:
✓ BS EN 1992/1993 (Structural Eurocodes) - Full compliance
✓ BS 7671 (Electrical) - 18th Edition compliance
✓ BS EN 12845 (Sprinklers) - LPC certification pending
✓ CIBSE Guidelines - Design and commissioning compliance
✓ CWCT (Curtain Wall) - Installation to standard

Certification Status:
• LEED Gold - On track, 62 points projected
• EPC Rating - Target B rating
• BREEAM - Not applicable to this project`,
        
        nonConformances: `NON-CONFORMANCE REGISTER:

NCR-047: Electrical Containment Alignment
Date Raised: [Current Month - 2 weeks]
Description: Cable tray installation in Level 3 plant room deviated 50mm from drawing alignment due to clash with ductwork.
Root Cause: Design coordination issue not identified during BIM review
Corrective Action: Re-route containment and update as-built drawings
Status: CLOSED - Rework completed and verified
Cost Impact: £450 (absorbed in subcontractor scope)

NCR-048: Drainage Gradient
Date Raised: [Current Month - 10 days]
Description: Section of above-ground drainage on Level 4 not achieving required 1:40 fall.
Root Cause: Structural deflection tolerance not accounted for in installation
Corrective Action: Adjust hanger heights to achieve required gradient
Status: CLOSED - Re-tested and approved
Cost Impact: Nil (covered under plumbing package)

NCR-049: Fire Stopping Gap
Date Raised: [Current Month - 5 days]
Description: Fire stopping around cable penetration in Level 2 riser showed 3mm gap.
Root Cause: Shrinkage of intumescent sealant during curing
Corrective Action: Apply additional sealant layer and re-inspect
Status: CLOSED - Re-inspected and approved
Cost Impact: Nil (warranty claim to supplier)

Preventive Actions Implemented:
• Enhanced BIM clash detection protocol for MEP coordination
• Additional hold points for drainage gradient verification
• Fire stopping installation training for all applicable trades`,
        
        materialTesting: `MATERIAL TESTING RESULTS:

Concrete Testing:
• Mix Design: C40/50, 400kg cement, w/c ratio 0.42
• Slump Tests: 18 tests conducted, all within 75-150mm tolerance
• Cube Strengths (28-day):
  - Target: 40 N/mm² | Achieved: Average 44.2 N/mm²
  - Minimum result: 41.5 N/mm² (exceeds requirement)
  - Maximum result: 47.8 N/mm²
• Compliance Rate: 100%

Structural Steel:
• Mill Certificates: All 850 tonnes verified
• Grade Verification: S355 J2 confirmed for all primary members
• Coating Thickness: Intumescent DFT verified (average 1.8mm)
• Bolt Testing: M20 8.8 grade verification complete
• Weld Testing: 15% UT sample, 100% pass rate

Reinforcement:
• Mill Certificates: B500B grade verification complete
• Bar Sampling: Tensile tests exceed requirements
• Coating: Epoxy coating verified for exposed areas

Waterproofing:
• Membrane Adhesion: Pull-off tests exceed 0.8 N/mm²
• Thickness Verification: Average 2.1mm (spec: 2.0mm min)
• Water Test: 48-hour ponding test passed

Curtain Wall:
• Glazing: Low-E coating verification complete
• Seals: Compression tests within tolerance
• Hardware: Load testing certificates verified`,
        
        qualityImprovements: `QUALITY IMPROVEMENT INITIATIVES:

1. Enhanced Pre-Inspection Checklist
Implemented trade-specific pre-inspection checklists to ensure work is ready for formal QA inspection. Result: 15% reduction in inspection failures.

2. Quality Workshop Programme
Weekly quality workshops implemented for all trades focusing on common defect prevention. Topics covered this month:
• Fire stopping best practices
• Cable management and containment standards
• Sealant application techniques
• Documentation requirements

3. Defect Prevention Analysis
Root cause analysis conducted on all NCRs to identify systemic issues. Key findings implemented:
• Improved BIM clash detection protocols
• Enhanced coordination meeting agenda
• Trade-specific quality training materials

4. Supplier Quality Reviews
Conducted quarterly supplier performance reviews with key material suppliers. Actions:
• Fire stopping supplier provided additional training
• Curtain wall supplier enhanced QC documentation
• Concrete supplier optimized mix design

5. Digital Quality Management
Transitioned to Procore for quality documentation:
• Real-time inspection tracking
• Photo documentation requirements
• Automated NCR workflows
• Dashboard reporting for management review`,
        
        // Metrics
        totalInspections: '127',
        inspectionsPassed: '124',
        inspectionsFailed: '3',
        successRate: '97.6'
      },
      safetyCompliance: {
        incidentReports: `SAFETY INCIDENT SUMMARY:

Reportable Incidents: ZERO
Lost Time Injuries: ZERO
Medical Treatment Cases: ZERO
First Aid Cases: 2 (minor cuts, treated on site)
Near Misses Reported: 3 (all investigated and closed)
Dangerous Occurrences: ZERO

Current Safety Record: 180 consecutive days without a lost-time injury

Near Miss Reports This Period:

NM-2024-047: Unsecured Material
Date: [Current Month - 3 weeks]
Description: Sheet material found unsecured on Level 6 during high winds
Investigation: Material left by finishing trade during break
Action: Toolbox talk on material securing, wind protocol reminder
Status: CLOSED

NM-2024-048: Trailing Cable
Date: [Current Month - 2 weeks]
Description: Extension cable across walkway in basement car park
Investigation: Electrician failed to use overhead routing
Action: Cable management training, additional cable stands procured
Status: CLOSED

NM-2024-049: Ladder Placement
Date: [Current Month - 1 week]
Description: Ladder observed at incorrect angle without stabiliser
Investigation: Worker took shortcut for quick access task
Action: Ladder safety refresher training for all personnel
Status: CLOSED

Total Labour Hours This Period: 28,800 hours
Cumulative Project Hours: 156,000 hours
Incident Rate (TRIR): 0.00`,
        
        complianceStatus: `REGULATORY COMPLIANCE STATUS:

Health & Safety Executive (HSE):
✓ F10 Notification submitted and acknowledged
✓ Construction Phase Plan approved and implemented
✓ Principal Contractor duties fulfilled
✓ CDM Regulations 2015 full compliance
✓ No enforcement notices or improvement notices received

OSHA Equivalent Standards:
✓ Working at height regulations compliance
✓ Confined space entry procedures in place
✓ COSHH assessments completed for all substances
✓ Manual handling assessments documented
✓ Noise and vibration assessments current

Site-Specific Compliance:
✓ Fire safety plan implemented and tested
✓ Emergency evacuation procedures established
✓ First aid provisions adequate (3 trained first aiders)
✓ Welfare facilities compliant (toilets, rest areas, drying room)
✓ Site security and access control operational

Recent Inspections:
• HSE Spot Check: [Last Month] - No issues identified
• Client Safety Audit: [Current Month] - Score 96%
• Insurance Safety Audit: [Current Month] - Score 94%
• Internal Safety Audit: Weekly - Average score 97%

Certification Status:
✓ Site Manager SMSTS certified
✓ All supervisors SSSTS certified
✓ CSCS cards verified for all operatives
✓ Specialist certifications (PASMA, IPAF) current`,
        
        safetyInspections: `SAFETY INSPECTION PROGRAMME:

Daily Inspections:
• Site Manager walkabout: 7:00 AM start-up inspection
• Safety Officer patrol: Continuous throughout day
• Housekeeping checks: End of each shift
• Scaffold inspections: Before first use each day

Weekly Inspections:
• Formal site safety inspection: Every Monday AM
• Plant and equipment checks: Every Wednesday
• Fire safety equipment: Every Friday
• Welfare facilities: Every Thursday

Monthly Inspections:
• Comprehensive site audit: First Monday of month
• Scaffold detailed inspection: Mid-month
• Electrical installation check: Monthly by competent person
• Emergency equipment test: Monthly drill

Inspection Results This Period:

Weekly Safety Inspections: 4 conducted
• Average score: 94%
• Issues identified: 12 (all closed within 24 hours)
• Recurring issues: None

Scaffold Inspections: 8 conducted
• All scaffolds tagged GREEN (safe for use)
• Minor adjustments: 3 (guardrail tightening)
• Major issues: None

Plant Inspections: 15 conducted
• All plant cleared for operation
• Defects identified: 2 (repaired same day)
• Plant removed from service: None

Fire Safety Checks: 4 conducted
• Fire points: All compliant
• Extinguisher checks: All current
• Escape routes: Clear and signed`,
        
        ppeCompliance: `PPE COMPLIANCE STATUS:

Mandatory PPE Requirements:
✓ Safety helmet (with chin strap for height work): 100% compliance
✓ High-visibility vest/jacket: 100% compliance
✓ Safety footwear (steel toe): 100% compliance
✓ Safety glasses (all work areas): 100% compliance
✓ Gloves (task-appropriate): 100% compliance

Task-Specific PPE:
✓ Harness and lanyard (height work): 100% compliance
✓ Hearing protection (plant operations): 100% compliance
✓ Dust masks (cutting/grinding): 100% compliance
✓ Face shields (welding/grinding): 100% compliance
✓ Chemical-resistant gloves (sealants): 100% compliance

PPE Provision This Period:
• New hard hats issued: 15 (replacement programme)
• High-vis vests issued: 25 (new workers + replacements)
• Safety glasses issued: 40 (disposable for visitors)
• Gloves issued: 200 pairs (various types)
• Hearing protection: 50 sets

PPE Inspection Results:
• Harnesses inspected: 35 (all passed)
• Lanyards inspected: 35 (2 replaced due to wear)
• Hard hats checked: All (8 replaced due to damage)
• Safety boots checked: All compliant

Initiatives:
• "PPE Champion" programme recognizing consistent compliance
• New entrant PPE pack introduced
• Enhanced PPE storage facilities installed`,
        
        safetyTraining: `SAFETY TRAINING COMPLETED THIS PERIOD:

Site Inductions:
• New worker inductions: 25 personnel
• Visitor inductions: 18 visitors
• Refresher inductions: 12 personnel (6-month refresh)

Toolbox Talks Delivered:
Week 1: Working at Height Refresher (45 attendees)
Week 2: Manual Handling - Curtain Wall Panels (32 attendees)
Week 3: Electrical Safety Awareness (28 attendees)
Week 4: Fire Safety and Emergency Procedures (52 attendees)

Specialist Training:
• PASMA (Mobile Scaffold): 6 workers certified
• IPAF (Scissor Lift): 8 workers certified
• Confined Space Entry: 4 workers trained
• First Aid Refresher: 2 workers updated
• Fire Warden Training: 3 new wardens appointed

Certification Updates:
• CSCS cards renewed: 8 workers
• SMSTS refresher: 1 supervisor
• SSSTS completion: 2 new supervisors

Training Hours This Period:
• Induction training: 50 hours
• Toolbox talks: 12 hours
• Specialist courses: 96 hours
• Total training hours: 158 hours

Planned Training (Next Period):
• Hot works permit training
• Crane signaller refresher
• COSHH awareness update
• Emergency response drill`,
        
        safetyImprovements: `SAFETY IMPROVEMENTS IMPLEMENTED:

Physical Improvements:

1. Enhanced Edge Protection (Level 7-8)
Installed upgraded guardrail system with self-closing gates and toe boards to all leading edges on upper floors. Cost: £8,500

2. Improved Site Lighting
Added 12 LED tower lights to basement parking and north elevation work areas to improve visibility for evening work. Cost: £4,200

3. Upgraded Welfare Facilities
Expanded drying room capacity and installed additional hand washing stations near work fronts. Cost: £3,800

4. Fall Prevention Anchor Points
Installed permanent roof anchor system (12 points) for safe maintenance access. Cost: £6,500

5. Traffic Management
Implemented one-way vehicle system with new signage and pedestrian barriers. Cost: £2,100

Process Improvements:

1. Pre-Task Planning
Enhanced STARRT (Safety Task Analysis Risk Reduction Talk) cards with QR code linking to method statements.

2. Near Miss Incentive Programme
Introduced recognition programme for near miss reporting, resulting in 40% increase in reports.

3. Daily Safety Briefing Format
Restructured morning briefings to include stretch and flex programme and task-specific hazard review.

4. Subcontractor Safety Integration
Implemented weekly subcontractor safety coordinator meetings to improve communication and coordination.

5. Digital Safety Documentation
Launched safety app for permit-to-work system and inspection recording, improving documentation efficiency by 60%.`,
        
        // Metrics
        daysWithoutIncident: '180',
        totalLaborHours: '156000',
        nearMisses: '3',
        trainingHours: '158'
      },
      schedulePerformance: {
        criticalPath: `CRITICAL PATH ANALYSIS:

Current Critical Path Sequence:
1. HVAC Equipment Installation → 2. Ductwork (Floors 4-8) → 3. Ceiling Grid → 4. MEP Second Fix → 5. Floor Finishes → 6. Practical Completion

Critical Path Duration: 18 weeks remaining

Float Analysis:
• Structural works: 5 days positive float (complete)
• Curtain wall: 3 days positive float
• HVAC ductwork: 0 days float (CRITICAL)
• Interior partitions: 4 days positive float

Key Critical Activities This Period:
• HVAC AHU delivery and installation - On track
• Level 4-6 ductwork installation - In progress
• Fire damper installation and testing - Commenced

Risk to Critical Path:
• HVAC equipment delivery - MEDIUM
• Skilled HVAC labour availability - LOW`,
        
        scheduleVariances: `SCHEDULE VARIANCE ANALYSIS:

Overall Schedule Performance:
• Schedule Variance: +3 days ahead
• Schedule Performance Index (SPI): 1.03

Variance by Work Package:
• Structural Works: +5 days ahead
• Building Envelope: +3 days ahead
• MEP Rough-In: +2 days ahead
• Interior Finishes: On programme

Factors Contributing to Favourable Variance:
• Efficient structural steel erection
• Good weather during critical envelope works
• Effective resource management
• Strong subcontractor performance

Schedule Recovery Actions Implemented:
• HVAC equipment delay: Weekend working approved
• Weather days: Selective overtime implemented`,
        
        milestonePerformance: `MILESTONE PERFORMANCE TRACKER:

COMPLETED MILESTONES:
✓ Contract Award - On time
✓ Site Establishment - 2 weeks early
✓ Piling Complete - On time
✓ Basement Structure - 1 week early
✓ Steel Frame Complete - 1 week early
✓ Roof Watertight - On time

IN PROGRESS MILESTONES:
◐ Building Envelope Complete - 90% (On time)
◐ MEP Rough-In Complete - 75% (2 days early)

UPCOMING MILESTONES:
○ Interior Partitions Complete - February 2025
○ MEP Second Fix Complete - April 2025
○ Lifts Operational - April 2025
○ Practical Completion - June 2025`,
        
        resourceImpact: `RESOURCE IMPACT ON SCHEDULE:

Labour Resources:
• Current Workforce: 85 personnel
• Peak Workforce Planning: 120 personnel (Month +3)
• Labour Utilization: 95% (Target: 90%)

Trade Availability Assessment:
• MEP trades: Fully resourced
• Finishing trades: Mobilizing as planned
• Specialists: Scheduled for key activities

Equipment Resources:
• Tower crane: Final month of use
• Material hoist: Extended hire confirmed
• Scissor lifts: 6 units (adequate)

Resource Optimization Actions:
• Labour levelling through activity sequencing
• Equipment sharing between subcontractors
• Just-in-time material delivery`,
        
        recoveryStrategies: `SCHEDULE RECOVERY STRATEGIES:

Implemented Recovery Actions:

1. Weather Delay Recovery (2 days lost)
   Strategy: Selective overtime on weekend
   Result: 2 days recovered, no cost impact

2. HVAC Equipment Delay Recovery
   Strategy: Re-sequence ductwork installation
   Result: Forecast to recover 8 of 10 days delay

Contingency Plans Available:

Plan A: Extended Working Hours (07:00-21:00 weekdays, Saturday pre-approved)
Plan B: Additional Resources (Framework agreements with labour agencies)
Plan C: Scope Re-sequencing (Alternative installation sequences identified)`,
        
        cashflowImpact: `CASH FLOW & SCHEDULE ALIGNMENT:

Payment Application Status:
• Application #9 submitted: £680,000
• Cumulative certified: £7,225,000
• Percentage of contract: 85%

Cash Flow Performance:
• Planned cumulative: £7,100,000
• Actual cumulative: £7,225,000
• Variance: +£125,000 (ahead of plan)

Cash Flow Status:
• All subcontractor payments within terms
• No funding constraints anticipated
• Client payment history: 100% on time`,
        
        // Metrics
        originalDuration: '540',
        revisedDuration: '537',
        spi: '1.03'
      },
      resourceUtilization: {
        laborAnalysis: `LABOUR RESOURCE ANALYSIS:

Current Workforce: 85 Personnel
• Site Management: 6
• Supervision: 8
• Skilled Trades: 52
• General Labour: 14
• Specialists: 5

Key Trade Breakdown:
• Electricians: 14
• Plumbers: 8
• HVAC Mechanics: 10
• Carpenters/Joiners: 8
• Glaziers: 6

Labour Productivity Metrics:
• Productivity index: 1.09 (112% of target)
• Utilization rate: 95%

Workforce Planning:
• Next month forecast: 95 personnel
• Peak workforce: 120 personnel (Month +3)`,
        
        equipmentAnalysis: `EQUIPMENT RESOURCE ANALYSIS:

Major Plant on Site:

Tower Crane (Liebherr 132 EC-H)
• Status: Operational (final month)
• Utilization: 75%

Material Hoist (Alimak SC 65/32)
• Status: Operational, 90% utilization
• Extended hire confirmed through completion

Scissor Lifts (6 units)
• Electric: 4 | Diesel: 2
• Utilization: 85%

Telehandlers (2 units)
• Utilization: 70%

Equipment Maintenance:
• Preventive maintenance: 100% on schedule
• Equipment downtime: 0.5%`,
        
        materialManagement: `MATERIAL MANAGEMENT SUMMARY:

Procurement Status:
• Total procurement value: £3,200,000
• Committed to date: £3,150,000 (98%)
• Delivered to date: £2,900,000 (91%)

Key Material Deliveries This Period:
• Curtain wall units (floors 7-8): ✓
• HVAC ductwork materials: ✓
• Electrical distribution boards: ✓
• Ceiling grid system: ✓

Material Quality:
• Inspection pass rate: 99%
• On-time deliveries: 98%

Waste Management:
• Total waste: 85 tonnes
• Recycled: 94% (Target: 75%)`,
        
        optimizationStrategies: `RESOURCE OPTIMIZATION STRATEGIES:

1. Lean Construction
• Plan Percent Complete (PPC): 89%
• Constraints removed: 11 of 12

2. Workflow Optimization
• Reduced double-handling by 40%
• Reduced trade interference by 30%

3. Equipment Sharing
• MEWPs shared between 3 subcontractors
• Reduced equipment hire costs by 15%

4. Digital Resource Management
• Real-time labour deployment tracking
• Equipment GPS monitoring`,
        
        performanceBenchmarks: `PERFORMANCE BENCHMARKS:

Labour Productivity:
• Project: 0.54 m²/hr vs Industry: 0.45 m²/hr (+20%)

Cost Performance:
• Project: £62/m² vs Budget: £65/m² (-5% favourable)

Schedule Performance:
• SPI: 1.03 vs Industry avg: 0.95 (+8%)

Quality Performance:
• First-time pass rate: 98% vs Target: 95%

Safety Performance:
• TRIR: 0.00 vs Industry: 3.5

Equipment Efficiency:
• Utilization: 87% vs Target: 80%`,
        
        // Table data
        labour: [
          { id: 'row_1', sn: '1', workerType: 'Site Management', workHours: '264.0' },
          { id: 'row_2', sn: '2', workerType: 'Supervision', workHours: '352.0' },
          { id: 'row_3', sn: '3', workerType: 'Electricians', workHours: '2,464.0' },
          { id: 'row_4', sn: '4', workerType: 'Plumbers', workHours: '1,408.0' },
          { id: 'row_5', sn: '5', workerType: 'HVAC Mechanics', workHours: '1,760.0' },
          { id: 'row_6', sn: '6', workerType: 'Carpenters', workHours: '1,408.0' },
          { id: 'row_7', sn: '7', workerType: 'General Labour', workHours: '2,464.0' }
        ],
        materialsOnSite: [
          { id: 'row_1', sn: '1', item: 'Drywall Sheets', description: '12.5mm plasterboard', quantity: '2,400 sheets' },
          { id: 'row_2', sn: '2', item: 'Metal Studs', description: '70mm C-studs and track', quantity: '3,500 lin.m' },
          { id: 'row_3', sn: '3', item: 'Electrical Cable', description: 'Various sizes, LSOH', quantity: '8,500 metres' },
          { id: 'row_4', sn: '4', item: 'HVAC Ductwork', description: 'Galvanized sheet', quantity: '450 m²' },
          { id: 'row_5', sn: '5', item: 'Ceiling Grid', description: 'Exposed grid system', quantity: '2,800 m²' }
        ],
        plantOnSite: [
          { id: 'row_1', sn: '1', item: 'Tower Crane', description: 'Liebherr 132 EC-H', quantity: '1', hiredOwned: 'Hired' },
          { id: 'row_2', sn: '2', item: 'Material Hoist', description: 'Alimak SC 65/32', quantity: '1', hiredOwned: 'Hired' },
          { id: 'row_3', sn: '3', item: 'Scissor Lift', description: 'JLG 3246ES', quantity: '6', hiredOwned: 'Hired' },
          { id: 'row_4', sn: '4', item: 'Telehandler', description: 'JCB 535-125', quantity: '2', hiredOwned: 'Owned' }
        ],
        materialsNeeded: [
          { id: 'row_1', sn: '1', item: 'Ceiling Tiles', description: '600x600 mineral fibre', quantity: '4,200 tiles', dateNeeded: '2024-12-15' },
          { id: 'row_2', sn: '2', item: 'Floor Tiles', description: 'Porcelain 600x600', quantity: '850 m²', dateNeeded: '2025-01-10' },
          { id: 'row_3', sn: '3', item: 'Sanitary Ware', description: 'WCs, basins', quantity: '96 sets', dateNeeded: '2025-01-20' },
          { id: 'row_4', sn: '4', item: 'Light Fittings', description: 'LED panels', quantity: '680 units', dateNeeded: '2025-02-01' }
        ],
        plantNeeded: [
          { id: 'row_1', sn: '1', item: 'Boom Lift', description: 'Articulating, 60ft', quantity: '2', dateNeeded: '2024-12-01' },
          { id: 'row_2', sn: '2', item: 'Floor Scrubber', description: 'Ride-on', quantity: '1', dateNeeded: '2025-05-01' }
        ]
      },
      budgetAnalysis: {
        varianceAnalysis: `BUDGET VARIANCE ANALYSIS:

Overall Financial Position:
• Original Contract Value: £8,500,000
• Approved Variations: £285,000
• Revised Contract Value: £8,785,000
• Forecast Final Cost: £8,650,000
• Projected Variance: £135,000 UNDER budget
• Cost Performance Index (CPI): 1.08

Variance by Cost Category:

Labour Costs: £30,000 favourable
• Reason: Higher productivity than planned

Material Costs: £50,000 favourable
• Reason: Competitive procurement, minimal waste

Subcontract Costs: £20,000 favourable
• Reason: Competitive tendering

Equipment & Plant: £15,000 favourable
• Reason: Efficient equipment utilization`,
        
        changeOrders: `CHANGE ORDER REGISTER:

Approved Change Orders: £285,000

CO-001: Enhanced Security Systems - £125,000 ✓
CO-002: HVAC Upgrades - £95,000 ✓
CO-003: Additional Power Capacity - £65,000 ✓

Pending Change Orders: £80,000 (estimated)

CO-004: Roof Plant Screening - £35,000 (pending)
CO-005: Enhanced Lobby Finishes - £45,000 (pending)

Change Order Summary:
• Total approved within contingency
• Contingency remaining: £127,500`,
        
        cashFlow: `CASH FLOW MANAGEMENT:

Application #9 (Current Period):
• Gross value: £680,000
• Net application: £646,000

Cumulative Position:
• Total certified: £7,225,000
• Total received: £6,863,750
• Retention held: £361,250

Subcontractor & Supplier Payments:
• All payments within terms
• No disputes outstanding

Cash Flow Status:
• Working capital: Adequate
• No constraints anticipated`,
        
        costForecast: `COST FORECAST & FINAL ACCOUNT:

Forecast Final Account:
• Direct Costs: £8,135,000
• Indirect Costs: £465,000
• Risk Allowance: £50,000
• Total Forecast: £8,650,000

Contract Position:
• Revised contract value: £8,785,000
• Projected margin: £135,000

Final Account Timeline:
• Practical completion: June 2025
• Final account agreement: September 2025
• Retention release: June 2026`,
        
        // Metrics
        originalBudget: '£8,500,000',
        revisedBudget: '£8,785,000',
        laborCosts: '£2,820,000',
        materialCosts: '£3,150,000',
        equipmentCosts: '£385,000',
        overheadCosts: '£515,000'
      },
      riskAssessment: {
        technicalDesignRisks: `TECHNICAL & DESIGN RISKS:

Risk T1: MEP Coordination Complexity - AMBER
• Description: Complex MEP routing in ceiling voids
• Mitigation: Weekly BIM coordination, enhanced clash detection
• Owner: MEP Coordinator

Risk T2: Building Envelope Performance - GREEN
• Description: Curtain wall thermal performance
• Status: Testing completed satisfactorily
• Owner: Envelope Subcontractor

Risk T3: Fire Engineering Compliance - GREEN
• Description: Fire strategy implementation
• Status: Strategy approved, installation progressing
• Owner: Fire Subcontractor`,
        
        financialCommercialRisks: `FINANCIAL & COMMERCIAL RISKS:

Risk F1: Material Price Escalation - AMBER
• Description: Market volatility affecting prices
• Status: Major procurement complete
• Mitigation: Fixed-price subcontracts

Risk F2: Subcontractor Financial Stability - GREEN
• Description: Key subcontractor insolvency risk
• Mitigation: Regular financial monitoring
• Owner: Commercial Manager

Risk F3: Variation Account Growth - AMBER
• Description: Client-instructed changes
• Status: £285,000 approved, £80,000 pending
• Mitigation: Robust change control process`,
        
        environmentalExternalRisks: `ENVIRONMENTAL & EXTERNAL RISKS:

Risk E1: Adverse Weather - AMBER
• Description: Weather impact on exterior works
• Status: 2 days lost this period
• Mitigation: Weather monitoring, flexible scheduling

Risk E2: Adjacent Property Impact - GREEN
• Description: Construction impact on neighbours
• Status: No complaints received
• Mitigation: Vibration monitoring, communication

Risk E3: Utility Service Disruption - GREEN
• Description: Power/water supply interruption
• Status: Permanent supplies connected
• Mitigation: Generator backup, water storage`,
        
        operationalResourceRisks: `OPERATIONAL & RESOURCE RISKS:

Risk O1: Skilled Labour Availability - AMBER
• Description: Specialist trades for fit-out
• Status: Key trades secured
• Mitigation: Early engagement, framework agreements

Risk O2: Equipment Breakdown - GREEN
• Description: Critical plant failure
• Status: All equipment well-maintained
• Mitigation: Preventive maintenance programme

Risk O3: Key Personnel Absence - GREEN
• Description: Loss of key project personnel
• Mitigation: Succession planning in place`,
        
        otherRisks: `OTHER RISKS:

Risk X1: Technology Systems Failure - GREEN
• Description: BIM or PM system failure
• Mitigation: Regular backups, IT support

Risk X2: Security Breach - GREEN
• Description: Site security compromise
• Mitigation: 24/7 security, CCTV, access control`,
        
        highPriorityAnalysis: `HIGH PRIORITY RISK ANALYSIS:

Top 5 Risks Requiring Active Management:

1. HVAC Equipment Delivery (AMBER)
   Status: 10-day delay being recovered
   
2. Weather Impact on Exterior Works (AMBER)
   Status: 2 days lost, recovered through overtime
   
3. Material Price Escalation (AMBER)
   Status: Procurement 98% complete
   
4. Skilled Labour Availability (AMBER)
   Status: Resources confirmed
   
5. MEP Coordination (AMBER)
   Status: Active BIM coordination`,
        
        mitigationStrategies: `RISK MITIGATION STRATEGIES:

Proactive Strategies:
• Weekly risk review meetings
• Traffic light status reporting
• Resource buffering (labour, equipment, materials)
• Alternative planning and backup suppliers

Reactive Strategies:
• Incident response procedures
• Acceleration options
• Escalation procedures with defined triggers`,
        
        contingencyPlans: `CONTINGENCY PLANS:

Schedule Contingency:
• Plan S1: Overtime Working (>3 days slippage)
• Plan S2: Additional Resources (>5 days slippage)
• Plan S3: Scope Re-sequencing (critical path impact)

Cost Contingency:
• Plan C1: Value Engineering (>5% overrun)
• Plan C2: Contingency Release (£127,500 remaining)

Quality Contingency:
• Plan Q1: Enhanced Inspection
• Plan Q2: Specialist Support`,
        
        riskMonitoring: `RISK MONITORING PROCEDURES:

Daily: Safety walkabouts, weather review, attendance
Weekly: Risk register review, programme status, cost report
Monthly: Comprehensive risk review, lessons learned

Key Performance Indicators:
• Number of open risks: 18
• High priority risks: 5
• Mitigation completion rate
• Cost and schedule variance`,
        
        // Metrics
        totalRisks: '18',
        highPriorityRisks: '5',
        risksMitigated: '13',
        residualRiskIndex: '0.78',
        technicalRisks: '3',
        financialRisks: '3',
        environmentalRisks: '3',
        operationalRisks: '3',
        otherRisks: '2'
      },
      nextPhase: {
        scopeOfWork: `PHASE 4: INTERIOR FINISHES & COMMISSIONING

Scope Overview:
Phase 4 encompasses interior fit-out works, MEP second fix, commissioning, and preparation for occupancy.

Key Works:
• Interior partitions & ceilings (8,500 m²)
• Floor finishes - raised access, tiling, carpet (7,500 m²)
• Wall finishes and decoration
• MEP second fix (electrical, mechanical, plumbing, fire, BMS)
• Individual and integrated systems commissioning`,
        
        keyMilestones: `PHASE 4 MILESTONE SCHEDULE:

Month 13 (Dec 2024): Partition framing floors 1-3, ceiling grid floors 1-2
Month 14 (Jan 2025): Partition framing floors 4-6, first fix completion
Month 15 (Feb 2025): Partition framing floors 7-8, HVAC commissioning
Month 16 (Mar 2025): Floor finishes, electrical commissioning, lifts operational
Month 17 (Apr 2025): Decoration, BMS commissioning, fire system testing
Month 18 (May-Jun 2025): Final decoration, defects, practical completion

Key Dependencies:
• MEP rough-in → Second fix start
• Ceiling closure → Floor finish start`,
        
        riskManagement: `PHASE 4 RISK ASSESSMENT:

High Risks:
R1: Interior Finish Quality - AMBER
• Mitigation: Enhanced QA, mock-ups, trade supervision

R2: Commissioning Coordination - AMBER
• Mitigation: Detailed commissioning plan, witness testing

R3: Specialist Trade Availability - AMBER
• Mitigation: Early booking, framework agreements

Medium Risks:
• Material delivery timing
• Design changes
• Defects resolution volume`,
        
        qualitySafety: `PHASE 4 QUALITY & SAFETY:

Quality Standards:
• Plasterboard: Level 4 finish
• Painting: 2 coats minimum
• Tiling: Lippage <1mm
• MEP: BS 7671, CIBSE standards
• Fire: Third-party certification

Safety Focus Areas:
• Working at height (MEWPs, ladders)
• Manual handling (heavy materials)
• Hazardous substances (paints, adhesives)
• Electrical safety during commissioning`,
        
        financialDetails: `PHASE 4 FINANCIAL SUMMARY:

Budget Allocation:
• Interior Finishes: £1,450,000
• MEP Second Fix: £420,000
• Commissioning: £85,000
• Preliminaries: £95,000
• Contingency: £50,000
• Total Phase 4: £2,100,000

Cash Flow Forecast:
Month 13-14: £800,000
Month 15-16: £830,000
Month 17-18: £470,000`,
        
        coordination: `PHASE 4 COORDINATION:

Internal:
• Trade sequencing: ceiling → floor → finishes
• Daily coordination meetings (7:15 AM)
• Weekly progress reviews
• Monthly steering group

External:
• Client: Weekly progress meetings
• Building Control: Inspection schedule
• Utilities: Permanent connections
• Neighbours: Noise management`,
        
        // Metrics
        phaseName: 'Phase 4: Interior Finishes & Commissioning',
        startDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toISOString().split('T')[0],
        duration: '150'
      },
      // Section 12.0 - Progress Comparisons Photo Grids
      progressComparisons: {
        photoGrids: [
          {
            id: 'grid_sample_1',
            title: 'Foundation to Structure Progress',
            leftPhoto: { 
              id: 'left_sample_1', 
              url: null, 
              name: '', 
              label: 'Site excavation and foundation preparation showing piling operations and ground preparation works in progress during the early construction phase' 
            },
            rightPhoto: { 
              id: 'right_sample_1', 
              url: null, 
              name: '', 
              label: 'Completed 8-story structural steel frame with composite floor decks installed, demonstrating the full building envelope ready for cladding installation' 
            }
          },
          {
            id: 'grid_sample_2', 
            title: 'Exterior Envelope Development',
            leftPhoto: { 
              id: 'left_sample_2', 
              url: null, 
              name: '', 
              label: 'Structural frame with temporary weather protection showing the building prior to curtain wall installation commencement' 
            },
            rightPhoto: { 
              id: 'right_sample_2', 
              url: null, 
              name: '', 
              label: 'Completed curtain wall installation on floors 1-6 with high-performance glazing system providing weather-tight building envelope' 
            }
          },
          {
            id: 'grid_sample_3', 
            title: 'MEP Installation Progress',
            leftPhoto: { 
              id: 'left_sample_3', 
              url: null, 
              name: '', 
              label: 'Open floor plate showing structural deck prior to MEP installation, with riser locations and service zones marked out' 
            },
            rightPhoto: { 
              id: 'right_sample_3', 
              url: null, 
              name: '', 
              label: 'Completed MEP rough-in installation showing coordinated ductwork, cable trays, and pipework ready for ceiling closure' 
            }
          }
        ]
      },
      // Section 13.0 - Technical Drawings Tables
      technicalDrawings: {
        // Metrics
        totalDrawings: '312',
        revisions: '47',
        completedDrawings: '285',
        // Table data
        architectural: [
          { id: 'row_1', sn: '1', drawingTitle: 'Ground Floor General Arrangement', drawingNumber: 'A-100-001', revisionDate: '2024-09-15' },
          { id: 'row_2', sn: '2', drawingTitle: 'Typical Office Floor Plan', drawingNumber: 'A-100-002', revisionDate: '2024-09-20' },
          { id: 'row_3', sn: '3', drawingTitle: 'Roof Plan and Details', drawingNumber: 'A-100-003', revisionDate: '2024-10-05' },
          { id: 'row_4', sn: '4', drawingTitle: 'Building Sections', drawingNumber: 'A-200-001', revisionDate: '2024-09-18' },
          { id: 'row_5', sn: '5', drawingTitle: 'Curtain Wall Elevations', drawingNumber: 'A-300-001', revisionDate: '2024-10-12' },
          { id: 'row_6', sn: '6', drawingTitle: 'Core Details and Finishes', drawingNumber: 'A-400-001', revisionDate: '2024-10-20' }
        ],
        structural: [
          { id: 'row_1', sn: '1', drawingTitle: 'Foundation Layout Plan', drawingNumber: 'S-100-001', revisionDate: '2024-03-10' },
          { id: 'row_2', sn: '2', drawingTitle: 'Steel Frame General Arrangement', drawingNumber: 'S-200-001', revisionDate: '2024-06-15' },
          { id: 'row_3', sn: '3', drawingTitle: 'Typical Floor Framing Plan', drawingNumber: 'S-200-002', revisionDate: '2024-06-22' },
          { id: 'row_4', sn: '4', drawingTitle: 'Connection Details Sheet 1', drawingNumber: 'S-300-001', revisionDate: '2024-07-08' },
          { id: 'row_5', sn: '5', drawingTitle: 'Core Wall Reinforcement', drawingNumber: 'S-400-001', revisionDate: '2024-05-20' }
        ],
        mep: [
          { id: 'row_1', sn: '1', drawingTitle: 'Electrical Distribution Schematic', drawingNumber: 'E-100-001', revisionDate: '2024-08-15' },
          { id: 'row_2', sn: '2', drawingTitle: 'Typical Floor Power Layout', drawingNumber: 'E-200-001', revisionDate: '2024-09-05' },
          { id: 'row_3', sn: '3', drawingTitle: 'HVAC Ductwork Layout Level 1', drawingNumber: 'M-200-001', revisionDate: '2024-09-12' },
          { id: 'row_4', sn: '4', drawingTitle: 'Plant Room Arrangement', drawingNumber: 'M-100-001', revisionDate: '2024-08-28' },
          { id: 'row_5', sn: '5', drawingTitle: 'Plumbing Riser Diagram', drawingNumber: 'P-100-001', revisionDate: '2024-08-20' },
          { id: 'row_6', sn: '6', drawingTitle: 'Fire Sprinkler Layout Typical', drawingNumber: 'FP-200-001', revisionDate: '2024-09-25' }
        ],
        other: [
          { id: 'row_1', sn: '1', drawingTitle: 'Site Layout Plan', drawingNumber: 'SP-001', revisionDate: '2024-02-05' },
          { id: 'row_2', sn: '2', drawingTitle: 'Landscape General Arrangement', drawingNumber: 'L-100-001', revisionDate: '2024-10-01' },
          { id: 'row_3', sn: '3', drawingTitle: 'External Works Details', drawingNumber: 'CW-100-001', revisionDate: '2024-09-30' },
          { id: 'row_4', sn: '4', drawingTitle: 'Signage and Wayfinding', drawingNumber: 'SG-100-001', revisionDate: '2024-10-15' }
        ],
        customTables: []
      }
    };
  };

  // Populate report with sample data (includes API integration) - Enhanced for ALL fields
  const populateReport = async (reportData, setReportData, setReportSections, setSelectedPeriod, setSelectedSite, sitesFromState, selectedPhotos, setSelectedPhotos, setPhotoLabels, setImportedPhotos) => {
    try {
      console.log('🚀 Populating report with comprehensive sample data...');
      setIsGenerating(true); // Show loading state
      
      // Always generate fresh sample data to match current structure
      console.log('🔄 Generating fresh sample data to match current structure...');
      let sampleData = generateSampleReportData();
      
      // Convert bullet-point text to proper HTML for TiptapEditor
      console.log('📝 Converting sample data to HTML format for TiptapEditor...');
      sampleData = processDataForHtml(sampleData);
      
      // Store the fresh sample data in backend for future use
      try {
        await axios.post(`${API}/admin/sample-report-data`, {
          sections: sampleData
        }, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        console.log('💾 Fresh sample data stored in database, replacing outdated data');
      } catch (storeError) {
        console.warn('⚠️ Could not store sample data, continuing with generated data');
      }
      
      // Set basic report data including cover page fields
      setReportData({
        title: `Construction Progress Report - ${new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`,
        description: 'Comprehensive monthly progress report with sample construction data covering all project aspects',
        author: 'Sarah Chen, Site Engineer',
        signature: 'Sarah Chen'
      });
      
      // Set sample period (current month) - using UTC to avoid timezone issues
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth();
      
      // Create dates at noon UTC to avoid timezone edge cases
      const firstDay = new Date(Date.UTC(year, month, 1, 12, 0, 0));
      const lastDay = new Date(Date.UTC(year, month + 1, 0, 12, 0, 0));
      
      setSelectedPeriod({
        startDate: firstDay.toISOString().split('T')[0],
        endDate: lastDay.toISOString().split('T')[0]
      });
      
      // Set sample site from the sites already loaded in state (first available site)
      console.log('📍 Using sites from state:', sitesFromState);
      console.log(`📍 Number of sites in state: ${sitesFromState?.length || 0}`);
      
      if (sitesFromState && sitesFromState.length > 0) {
        const siteId = sitesFromState[0].id;
        const siteName = sitesFromState[0].site_name;
        console.log(`📍 Selecting site from state: ${siteName} (ID: ${siteId})`);
        
        if (typeof setSelectedSite === 'function') {
          setSelectedSite(siteId);
          console.log(`✅ setSelectedSite called with ID: ${siteId}`);
        } else {
          console.error('❌ setSelectedSite is not a function!', setSelectedSite);
        }
      } else {
        console.log('⚠️ No sites in state, trying to fetch from API...');
        // Fallback: try to fetch sites if not in state
        try {
          const sitesResponse = await axios.get(`${API}/admin/sites`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          const sites = sitesResponse.data;
          if (sites && sites.length > 0) {
            setSelectedSite(sites[0].id);
            console.log(`📍 Selected site from API: ${sites[0].site_name}`);
          }
        } catch (error) {
          console.warn('⚠️ Could not load sites for sample data');
        }
      }
      
      // Set all report sections with comprehensive data
      console.log('📝 Setting report sections with sample data...');
      console.log('Sample data structure:', sampleData);
      setReportSections(prev => ({ ...sampleData, reportOptions: prev?.reportOptions }));
      
      // Debug: Log what was set
      setTimeout(() => {
        console.log('🔍 Checking if data was set correctly...');
        console.log('Executive Summary should have:', sampleData.executiveSummary);
      }, 1000);
      
      // Load comprehensive sample photos with descriptions
      await loadSamplePhotos(setSelectedPhotos, setPhotoLabels, setImportedPhotos);
      
      console.log('✅ Report populated successfully with comprehensive sample data including:');
      console.log('   • Basic report information (title, description, dates, site)');
      console.log('   • All report sections with detailed content');
      console.log('   • Sample photos with descriptions');
      console.log('   • Complete form data across all tabs');
      
    } catch (error) {
      console.error('Error populating comprehensive report:', error);
      throw new Error('Error populating report with comprehensive sample data: ' + error.message);
    } finally {
      setIsGenerating(false); // Hide loading state
    }
  };

  // Load comprehensive sample photos from work diary with descriptions
  const loadSamplePhotos = async (setSelectedPhotos, setPhotoLabels, setImportedPhotos) => {
    try {
      console.log('📸 Loading comprehensive sample photos from work diary...');
      
      // Fetch available photos
      const response = await axios.get(`${API}/admin/work-diary-photos`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const photos = response.data;
      console.log(`Found ${photos.length} available photos for sample data`);
      
      if (photos.length > 0) {
        // Select up to 12 photos for sample data (2 pages at 6 per page)
        const maxPhotos = 12;
        const selectedCount = Math.min(maxPhotos, photos.length);
        
        // Sort by timestamp (most recent first) instead of random shuffle
        const sortedPhotos = [...photos].sort((a, b) => {
          const dateA = new Date(a.timestamp || a.created_at || 0);
          const dateB = new Date(b.timestamp || b.created_at || 0);
          return dateB - dateA;
        });
        
        const selectedPhotos = sortedPhotos.slice(0, selectedCount);
        const selectedPhotoIds = selectedPhotos.map(photo => photo.id);
        
        // Set selected photos for legacy compatibility
        setSelectedPhotos(selectedPhotoIds);
        
        // Set imported photos with full data for new system
        const importedPhotoData = selectedPhotos.map((photo, index) => ({
          id: photo.id,
          url: photo.gcs_url || photo.url,
          gcs_url: photo.gcs_url,
          name: photo.name || `sample_photo_${index + 1}`,
          description: photo.description || `Construction progress photo ${index + 1} documenting site activities and quality standards.`,
          timestamp: photo.timestamp || photo.created_at || new Date().toISOString(),
          location: photo.location || `Construction Site - Zone ${String.fromCharCode(65 + (index % 8))}` // A, B, C, etc.
        }));
        
        setImportedPhotos(importedPhotoData);
        
        // Create comprehensive sample labels and descriptions for selected photos
        const sampleLabels = {};
        const labels = [
          'Foundation Work Progress', 'Structural Framework', 'MEP Installation', 
          'Quality Inspection', 'Safety Protocols', 'Material Delivery',
          'Equipment Operation', 'Site Preparation', 'Interior Build-out',
          'Exterior Progress', 'Testing & Commissioning', 'Final Finishes',
          'Concrete Pour', 'Steel Erection', 'Roofing Works',
          'Cladding Installation', 'Window Fitting', 'Floor Installation',
          'Ceiling Works', 'Electrical Rough-In', 'Plumbing Installation',
          'HVAC Ductwork', 'Fire Protection', 'Landscaping'
        ];
        
        selectedPhotoIds.forEach((photoId, index) => {
          sampleLabels[photoId] = {
            label: `${labels[index % labels.length]} - Week ${Math.floor(index / 6) + 1}`,
            description: `Documentation of ${labels[index % labels.length].toLowerCase()} showing adherence to project specifications, safety protocols, and quality standards during this reporting period.`
          };
        });
        
        setPhotoLabels(sampleLabels);
        
        const pagesCount = Math.ceil(selectedCount / 6);
        console.log(`✅ Successfully loaded ${selectedCount} photos (${pagesCount} pages at 6 per page)`);
        console.log(`   • Photos added to imported photos for Progress Photos tab`);
        console.log(`   • Photo labels and descriptions generated`);
        console.log(`   • Sample data ready for testing all photo functionality`);
        
      } else {
        console.log('⚠️ No photos available for sample data');
        // Set empty arrays if no photos available
        setSelectedPhotos([]);
        setImportedPhotos([]);
        setPhotoLabels({});
      }
      
    } catch (error) {
      console.error('Error loading sample photos:', error);
      // Set empty arrays on error
      setSelectedPhotos([]);
      setImportedPhotos([]);
      setPhotoLabels({});
    }
  };

  return {
    // Generation State
    isGenerating,
    setIsGenerating,
    isGeneratedReportCurrent,
    setIsGeneratedReportCurrent,
    generatedReportData,
    setGeneratedReportData,
    showReportViewer,
    setShowReportViewer,
    showInlineViewer,
    setShowInlineViewer,
    currentReport,
    setCurrentReport,
    
    // Refs
    reportPreviewRef,
    
    // Data Fetching Functions
    fetchSites,
    fetchOrganizationSettings,
    
    // Report Generation Functions
    generateComprehensiveReport,
    generateReport,
    generateSampleReportData,
    populateReport,
    loadSamplePhotos
  };
};